import React, { useState } from "react";
import { motion } from "framer-motion";
import { houseFor } from "@/lib/houses";
import { mediaUrl } from "@/lib/api";
import { playAudioLogo } from "@/lib/audioLogo";
import { useNarrator } from "@/lib/narrator";

/**
 * Ceremonial card that opens with the user's House card back
 * and flips once to reveal the face image.
 *
 * Props:
 *   element      — "Fire" | "Water" | "Air" | "Earth"
 *   faceUrl      — mediaUrl-ready path or absolute URL
 *   title        — small caption below (optional)
 *   subtitle     — smaller caption (optional)
 *   upright      — bool (rotate face 180 if false)
 *   delay        — seconds before auto-flip. If null, waits for click.
 *   autoFlip     — bool (default true when delay is set)
 *   size         — "sm" | "md" | "lg"
 *   dataTestid   — string
 *   onFlip       — callback when flip completes
 */
export default function HouseCard({
  element = "Fire",
  faceUrl,
  title,
  subtitle,
  upright = true,
  delay = null,
  autoFlip = true,
  size = "md",
  dataTestid,
  onFlip,
}) {
  const [flipped, setFlipped] = useState(false);
  const house = houseFor(element);
  const { muted } = useNarrator();

  React.useEffect(() => {
    if (!autoFlip || delay === null) return;
    const t = setTimeout(() => {
      setFlipped(true);
      playAudioLogo({ muted });
    }, delay * 1000);
    return () => clearTimeout(t);
  }, [delay, autoFlip, muted]);

  const dims = {
    sm: "w-32 sm:w-40",
    md: "w-48 sm:w-56 lg:w-64",
    lg: "w-64 sm:w-80 lg:w-96",
  }[size] || "w-48";

  const face = faceUrl ? mediaUrl(faceUrl) : null;

  return (
    <div className={`${dims} aspect-square`} style={{ perspective: "1400px" }}>
      <motion.div
        data-testid={dataTestid}
        onClick={() => {
          if (!flipped) {
            setFlipped(true);
            playAudioLogo({ muted });
          }
        }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 1.6, ease: [0.25, 0.6, 0.3, 1] }}
        onAnimationComplete={() => flipped && onFlip && onFlip()}
        className="relative w-full h-full cursor-pointer"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Back — House artwork */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden"
          style={{ backfaceVisibility: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.55)" }}
        >
          <img
            src={house.back}
            alt={`${house.name} House card back`}
            className="w-full h-full object-cover select-none pointer-events-none"
            draggable={false}
          />
        </div>

        {/* Face */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden bg-black"
          style={{
            transform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
          }}
        >
          {face ? (
            <img
              src={face}
              alt={title || "card face"}
              className="w-full h-full object-cover select-none pointer-events-none"
              draggable={false}
              style={{ transform: upright ? "none" : "rotate(180deg)" }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#C8A96E]/50 font-overline">
              no face
            </div>
          )}
        </div>
      </motion.div>

      {(title || subtitle) && (
        <div className="mt-4 text-center">
          {title && <div className="font-heading italic text-base sm:text-lg text-[#F4EFE6]">{title}</div>}
          {subtitle && <div className="font-overline text-[#C8A96E]/70 mt-1">{subtitle}</div>}
        </div>
      )}
    </div>
  );
}
